import React, { useState } from 'react';
import { CreditCard as Edit3, Save, X, TrendingUp, DollarSign, Calendar, Percent } from 'lucide-react';
import type { FinancialKPIs } from '../../types/businessPlan';

interface KPIEditorProps {
  kpis: FinancialKPIs;
  onUpdate: (updatedKPIs: FinancialKPIs) => void;
  isLoading?: boolean;
  className?: string;
}

export const KPIEditor: React.FC<KPIEditorProps> = ({
  kpis,
  onUpdate,
  isLoading = false,
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedKPIs, setEditedKPIs] = useState<FinancialKPIs>(kpis);
  const [hasChanges, setHasChanges] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedKPIs(kpis);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedKPIs(kpis);
    setHasChanges(false);
  };

  const handleSave = () => {
    onUpdate(editedKPIs);
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleKPIChange = (field: keyof FinancialKPIs, value: number) => {
    const updated = { ...editedKPIs, [field]: value };
    setEditedKPIs(updated);
    setHasChanges(true);
    
    // Recalculate dependent KPIs
    if (field === 'ca_total') {
      updated.ca_monthly_avg = Math.round(value / 12);
      updated.ebitda = Math.round(value * (updated.marge_nette / 100));
    }
    
    if (field === 'marge_nette') {
      updated.ebitda = Math.round(updated.ca_total * (value / 100));
    }
    
    // Update risk indicator based on margins
    if (updated.marge_nette >= 15 && updated.point_mort_mois <= 12) {
      updated.risk_indicator = 'green';
    } else if (updated.marge_nette >= 8 && updated.point_mort_mois <= 18) {
      updated.risk_indicator = 'orange';
    } else {
      updated.risk_indicator = 'red';
    }
    
    setEditedKPIs(updated);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const kpiFields = [
    {
      key: 'ca_total' as const,
      label: 'Chiffre d\'Affaires Total',
      icon: DollarSign,
      format: (value: number) => formatCurrency(value),
      step: 1000,
      min: 0,
      max: 10000000,
      color: 'green'
    },
    {
      key: 'ca_monthly_avg' as const,
      label: 'CA Mensuel Moyen',
      icon: Calendar,
      format: (value: number) => formatCurrency(value),
      step: 100,
      min: 0,
      max: 1000000,
      color: 'blue',
      readonly: true
    },
    {
      key: 'ebitda' as const,
      label: 'EBITDA',
      icon: TrendingUp,
      format: (value: number) => formatCurrency(value),
      step: 1000,
      min: 0,
      max: 5000000,
      color: 'purple',
      readonly: true
    },
    {
      key: 'marge_brute' as const,
      label: 'Marge Brute (%)',
      icon: Percent,
      format: (value: number) => `${value.toFixed(1)}%`,
      step: 0.1,
      min: 0,
      max: 100,
      color: 'indigo'
    },
    {
      key: 'marge_nette' as const,
      label: 'Marge Nette (%)',
      icon: Percent,
      format: (value: number) => `${value.toFixed(1)}%`,
      step: 0.1,
      min: 0,
      max: 100,
      color: 'teal'
    },
    {
      key: 'point_mort_mois' as const,
      label: 'Point Mort (mois)',
      icon: Calendar,
      format: (value: number) => `${value} mois`,
      step: 1,
      min: 1,
      max: 60,
      color: 'orange'
    },
    {
      key: 'roi' as const,
      label: 'ROI (%)',
      icon: TrendingUp,
      format: (value: number) => `${value.toFixed(1)}%`,
      step: 0.1,
      min: -100,
      max: 500,
      color: 'pink'
    },
    {
      key: 'cashflow_cumule' as const,
      label: 'Cashflow Cumulé',
      icon: DollarSign,
      format: (value: number) => formatCurrency(value),
      step: 1000,
      min: -1000000,
      max: 5000000,
      color: 'cyan'
    }
  ];

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Indicateurs Clés de Performance</h3>
          <p className="text-sm text-gray-600">
            {isEditing ? 'Modifiez les valeurs en temps réel' : 'Cliquez sur modifier pour ajuster les indicateurs'}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Annuler</span>
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>Sauvegarder</span>
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              <span>Modifier</span>
            </button>
          )}
        </div>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiFields.map((field) => {
          const IconComponent = field.icon;
          const currentValue = isEditing ? editedKPIs[field.key] : kpis[field.key];
          
          return (
            <div
              key={field.key}
              className={`bg-gradient-to-br from-${field.color}-50 to-${field.color}-100 rounded-xl p-4 border border-${field.color}-200 transition-all duration-200 ${
                isEditing && !field.readonly ? 'hover:shadow-lg hover:scale-105' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 bg-${field.color}-600 rounded-lg flex items-center justify-center`}>
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                {field.readonly && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    Auto
                  </span>
                )}
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{field.label}</p>
                
                {isEditing && !field.readonly ? (
                  <div className="space-y-2">
                    <input
                      type="number"
                      value={currentValue}
                      onChange={(e) => handleKPIChange(field.key, parseFloat(e.target.value) || 0)}
                      step={field.step}
                      min={field.min}
                      max={field.max}
                      className="w-full px-2 py-1 text-lg font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Min: {field.format(field.min)}</span>
                      <span>Max: {field.format(field.max)}</span>
                    </div>
                  </div>
                ) : (
                  <p className={`text-2xl font-bold text-${field.color}-700`}>
                    {field.format(currentValue)}
                  </p>
                )}
              </div>
              
              {/* Change indicator */}
              {isEditing && hasChanges && editedKPIs[field.key] !== kpis[field.key] && (
                <div className="mt-2 flex items-center space-x-1 text-xs">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <span className="text-orange-600 font-medium">
                    {editedKPIs[field.key] > kpis[field.key] ? '+' : ''}
                    {((editedKPIs[field.key] - kpis[field.key]) / kpis[field.key] * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Risk Indicator */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              (isEditing ? editedKPIs : kpis).risk_indicator === 'green' ? 'bg-green-100' :
              (isEditing ? editedKPIs : kpis).risk_indicator === 'orange' ? 'bg-orange-100' : 'bg-red-100'
            }`}>
              <span className="text-lg">
                {(isEditing ? editedKPIs : kpis).risk_indicator === 'green' ? '✅' :
                 (isEditing ? editedKPIs : kpis).risk_indicator === 'orange' ? '⚠️' : '🚨'}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Indicateur de Risque</p>
              <p className={`text-sm ${
                (isEditing ? editedKPIs : kpis).risk_indicator === 'green' ? 'text-green-600' :
                (isEditing ? editedKPIs : kpis).risk_indicator === 'orange' ? 'text-orange-600' : 'text-red-600'
              }`}>
                {(isEditing ? editedKPIs : kpis).risk_indicator === 'green' ? 'Risque Faible' :
                 (isEditing ? editedKPIs : kpis).risk_indicator === 'orange' ? 'Risque Modéré' : 'Risque Élevé'}
              </p>
            </div>
          </div>
          
          {isEditing && hasChanges && (
            <div className="text-xs text-gray-600">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span>Mise à jour automatique du risque</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      {isEditing && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 <strong>Astuce :</strong> Les indicateurs marqués "Auto" se recalculent automatiquement 
            en fonction de vos modifications. L'indicateur de risque s\'ajuste en temps réel.
          </p>
        </div>
      )}
    </div>
  );
};